import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import JobCard from "../components/JobCard";
import StateSponsorBanner from "../components/StateSponsorBanner";
import { Search, MapPin, X, Filter } from "lucide-react";

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [location, setLocation] = useState(params.get("location") || "");
  const [industry, setIndustry] = useState(params.get("industry") || "");
  const [occupation, setOccupation] = useState(params.get("occupation") || "");
  const [registeredOnly, setRegisteredOnly] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/industries").then((r) => setIndustries(r.data));
  }, []);

  const load = async (p = 1) => {
    setLoading(true);
    const query = new URLSearchParams();
    if (q) query.set("q", q);
    if (location) query.set("location", location);
    if (industry) query.set("industry", industry);
    if (occupation) query.set("occupation", occupation);
    if (registeredOnly) query.set("registered_only", "true");
    query.set("page", p);
    query.set("limit", "12");
    const res = await api.get(`/jobs?${query.toString()}`);
    setJobs(res.data.jobs);
    setTotal(res.data.total);
    setPage(p);
    setLoading(false);
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [industry, occupation, registeredOnly]);

  useEffect(() => {
    const qp = params.get("q") || "";
    const lp = params.get("location") || "";
    const ip = params.get("industry") || "";
    const op = params.get("occupation") || "";
    setQ(qp); setLocation(lp); setIndustry(ip); setOccupation(op);
    load(1); // eslint-disable-next-line
  }, [params]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (location) p.set("location", location);
    if (industry) p.set("industry", industry);
    setParams(p);
  };

  const clear = () => {
    setQ(""); setLocation(""); setIndustry(""); setOccupation(""); setRegisteredOnly(false);
    setParams({});
  };

  const totalPages = Math.max(1, Math.ceil(total / 12));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="text-xs font-mono-alt uppercase text-yellow-500 tracking-widest mb-2">Job Search</div>
        <h1 className="font-display text-5xl md:text-6xl uppercase leading-none">
          {total} <span className="text-yellow-500">apprenticeships</span>
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="border border-zinc-800 bg-zinc-950/50 p-4 grid grid-cols-1 md:grid-cols-3 gap-2 mb-6">
        <div className="flex items-center gap-2 border border-zinc-800 px-3 focus-within:border-yellow-500 bg-black">
          <Search size={16} className="text-yellow-500" />
          <input data-testid="jobs-search-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Occupation, employer, skill…" className="flex-1 bg-transparent py-3 text-white placeholder:text-zinc-600 focus:outline-none" />
        </div>
        <div className="flex items-center gap-2 border border-zinc-800 px-3 focus-within:border-yellow-500 bg-black">
          <MapPin size={16} className="text-yellow-500" />
          <input data-testid="jobs-search-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="flex-1 bg-transparent py-3 text-white placeholder:text-zinc-600 focus:outline-none" />
        </div>
        <button data-testid="jobs-search-submit" className="bg-yellow-500 text-black font-bold uppercase tracking-wider py-3 hover:bg-yellow-400 transition">Search</button>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <aside className="border border-zinc-800 bg-zinc-950/50 p-5 h-fit">
          <div className="flex items-center gap-2 mb-4 text-xs font-mono-alt uppercase tracking-widest text-yellow-500">
            <Filter size={14} /> Filters
          </div>

          <div className="mb-6">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Industry</div>
            <div className="flex flex-col gap-1">
              <button
                data-testid="filter-industry-all"
                onClick={() => setIndustry("")}
                className={`text-left text-sm px-2 py-1 ${!industry ? "text-yellow-500 font-semibold" : "text-zinc-400 hover:text-white"}`}
              >All Industries</button>
              {industries.map((ind) => (
                <button
                  key={ind.name}
                  data-testid={`filter-industry-${ind.name}`}
                  onClick={() => setIndustry(ind.name)}
                  className={`text-left text-sm px-2 py-1 flex justify-between ${industry === ind.name ? "text-yellow-500 font-semibold" : "text-zinc-400 hover:text-white"}`}
                >
                  <span>{ind.name}</span><span className="text-zinc-600">{ind.count}</span>
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
            <input
              data-testid="filter-registered"
              type="checkbox"
              checked={registeredOnly}
              onChange={(e) => setRegisteredOnly(e.target.checked)}
              className="accent-yellow-500"
            />
            Registered Occupation only
          </label>

          {(industry || occupation || registeredOnly) && (
            <button data-testid="filter-clear" onClick={clear} className="mt-6 text-xs uppercase tracking-wider text-zinc-500 hover:text-yellow-500 flex items-center gap-1">
              <X size={12} /> Clear filters
            </button>
          )}
        </aside>

        <div className="md:col-span-3">
          <StateSponsorBanner state={location.toUpperCase().length === 2 ? location.toUpperCase() : ""} />
          {loading ? (
            <div className="text-zinc-500 py-20 text-center font-mono-alt uppercase text-sm">Loading&hellip;</div>
          ) : jobs.length === 0 ? (
            <div className="border border-zinc-800 p-12 text-center">
              <div className="font-display text-3xl uppercase mb-2">No matches</div>
              <div className="text-zinc-500">Try broadening your search.</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                {jobs.map((j, i) => (<JobCard key={j.id} job={j} index={i} />))}
              </div>
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      data-testid={`page-${i + 1}`}
                      onClick={() => load(i + 1)}
                      className={`w-10 h-10 border ${page === i + 1 ? "bg-yellow-500 text-black border-yellow-500" : "border-zinc-800 text-zinc-400 hover:border-yellow-500"} font-mono-alt text-sm`}
                    >{i + 1}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
